"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { PRO_PRICE_ANNUAL_PER_MONTH, PRO_PRICE_ANNUAL_TOTAL, PRO_PRICE_MONTHLY } from "@/lib/pricing";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import { useAuth } from "@/lib/auth-context";
import { useLocale } from "@/lib/locale-context";
import { API_BASE } from "@/lib/api";
import { isNativeApp } from "@/lib/native-app";
import { trackEvent } from "@/lib/tracking";
import { captureEvent } from "@/lib/tracking";
import SharedHeader from "@/components/SharedHeader";
import { formatPublishedAt, formatMonthDay } from "@/lib/blog-format";
import { Check, Crown, Loader2, X, CreditCard, ChevronRight, Bell, ShieldCheck, ScanEye, Copy as CopyIcon, Heart, Building2, Map as MapIcon, MessageSquare } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useConfirm } from "@/components/ConfirmDialog";
import { TrialDimAnim } from "@/components/animations/SceneAnimations";
import { SEGMENT_ON } from "@/lib/ui-classes";

// Confetti is browser-only (touches `window`); the dynamic import
// with ssr:false keeps the SSR pass on Cloudflare Workers from
// pulling in canvas / DOM globals that aren't available there.
const Confetti = dynamic(() => import("react-confetti"), { ssr: false });


interface LastPayment {
  status: string;
  id: number | null;
  payment_type: string | null;
  amount: number | null;
  currency: string | null;
  tokens_granted: number | null;
  new_balance: number | null;
  transaction: string | null;
  card_mask: string | null;
  created_at: string | null;
}

// GET /auth/trial/losses - what THIS user personally loses at trial
// end (counts + free caps applied server-side). Same source as the
// day-5 email, so the page and the email always tell one story.
interface TrialLosses {
  alerts_active: number;
  alerts_over_cap: number;
  alerts_instant: number;
  pins: number;
  pins_over_cap: number;
  properties: number;
  properties_over_cap: number;
  history_over_cap?: number;
}

interface SubscriptionInfo {
  provider: string | null;
  status: string | null;
  plan: string | null;
  ends_at: string | null; // ISO8601 UTC
  card: string | null; // masked PAN, e.g. "4169 12** **** 1234"
}

// Set right before the hard navigation to the Epoint hosted form.
// Epoint only redirects back on explicit success/error - a user who
// just presses the browser Back button returns with NO params, so this
// marker is the only way to recognize that return and tell them,
// honestly, that nothing was charged.
const CHECKOUT_PENDING_KEY = "menzil.checkout.pending";
// Same idea for the card-upfront trial's bank redirect: pressing Back
// from the card-registration page returns with no params at all.
const TRIALCARD_PENDING_KEY = "menzil.trialcard.pending";

export default function PricingPage() {
  const t = useT();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { locale } = useLocale();
  const { user, tokens, refreshTokens, setUser, trialMode, loading: authLoading } = useAuth();

  const isSubscriber = tokens?.is_subscriber;
  // Until the session and the token payload are in, we do not know which
  // page this is. Rendering the sales version meanwhile flashed the
  // upgrade pitch at people who already pay (owner, 2026-09-22: "when I
  // hard refresh, I see the free page for a fraction of a second").
  const planUnknown = authLoading || (!!user && tokens == null);
  // Inside the native mobile shell purchases are hidden entirely (store
  // billing policies forbid selling digital goods via web checkout
  // in-app). The page stays readable: balance, plan status, history.
  const [inApp, setInApp] = useState(false);
  useEffect(() => {
    setInApp(isNativeApp());
  }, []);
  // Annual is the default (Mobbin paywall study: yearly default = the
  // highest-LTV configuration; the monthly price stays visible as the
  // struck-through anchor). The toggle is one tap for monthly buyers.
  const [billing, setBilling] = useState<"monthly" | "annual">("annual");
  const isAnnual = billing === "annual";
  const proPrice = isAnnual ? PRO_PRICE_ANNUAL_PER_MONTH : PRO_PRICE_MONTHLY;

  // Billing segmented control: a single measured thumb slides between
  // the two options (the buttons themselves only change text color),
  // so the state change animates instead of teleporting. The control
  // mounts only after the auth/plan fetches resolve, so measurement
  // hangs off a callback ref (mount) + billing changes + resizes -
  // an effect keyed on billing alone never sees the late mount.
  const segRef = useRef<HTMLDivElement | null>(null);
  const billingRef = useRef(billing);
  billingRef.current = billing;
  const [thumb, setThumb] = useState<{ x: number; w: number } | null>(null);
  const measureSeg = useCallback(() => {
    const btn = segRef.current?.querySelector<HTMLButtonElement>(
      `[data-billing="${billingRef.current}"]`,
    );
    if (btn) setThumb({ x: btn.offsetLeft, w: btn.offsetWidth });
  }, []);
  const segMountRef = useCallback((el: HTMLDivElement | null) => {
    segRef.current = el;
    if (el) measureSeg();
  }, [measureSeg]);
  useLayoutEffect(() => {
    measureSeg();
    window.addEventListener("resize", measureSeg);
    return () => window.removeEventListener("resize", measureSeg);
  }, [measureSeg, billing]);

  const [busy, setBusy] = useState(false);
  const [trialBusy, setTrialBusy] = useState(false);
  const [confirmDlg, confirmEl] = useConfirm();
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Celebration state for the return from a Pro checkout (?sub=ok):
  // confetti + the Pro success modal. The token-pack return path
  // (?payment=) went with the token system (owner, 2026-10-18).
  const [showConfetti, setShowConfetti] = useState(false);
  const [winSize, setWinSize] = useState({ w: 0, h: 0 });

  // Pro-subscription success modal (separate copy from the token-pack
  // modal) shown on return from Epoint with ?sub=ok.
  const [showProSuccess, setShowProSuccess] = useState(false);

  // Personalized trial stakes - fetched only while a trial is running.
  // Every rendered line maps 1:1 to what sweep_expired_trials actually
  // enforces; zero-count lines never render (no invented loss).
  const [losses, setLosses] = useState<TrialLosses | null>(null);
  const onTrialNow = user?.pro_preview_days_left != null && !isSubscriber;
  useEffect(() => {
    if (!onTrialNow) {
      setLosses(null);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/auth/trial/losses`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: TrialLosses | null) => {
        if (!cancelled) setLosses(d);
      })
      .catch(() => { /* card falls back to the generic body */ });
    return () => {
      cancelled = true;
    };
  }, [onTrialNow]);

  // Subscription state for active subscribers: renewal date + masked
  // card, shown on the Pro card. Fetched from /api/subscription/status.
  const [subInfo, setSubInfo] = useState<SubscriptionInfo | null>(null);
  const [canceling, setCanceling] = useState(false);

  // Same flow as the profile page: the provider keeps Pro until the paid
  // period ends, so after the call we only refetch and relabel the date.
  async function cancelSubscription() {
    if (!(await confirmDlg({
      body: t("profile.cancel.confirm"),
      cancelLabel: t("confirm.sub.keep"),
      confirmLabel: t("confirm.sub.go"),
      danger: true,
    }))) return;
    setCanceling(true);
    try {
      await fetch(`${API_BASE}/subscription/cancel`, { method: "POST", credentials: "include" });
      const r = await fetch(`${API_BASE}/subscription/status`, { credentials: "include" });
      if (r.ok) setSubInfo(await r.json());
    } finally {
      setCanceling(false);
    }
  }

  // Return from the card-registration page (card-upfront trial).
  // ok: the webhook may lag the browser - refresh /auth/me twice so
  // the trial state appears without a manual reload. cancel: quiet,
  // honest, nothing was charged. Bare Back: the sessionStorage marker
  // is the only trace - same honest note.
  useEffect(() => {
    const p = searchParams?.get("trial_card");
    const strip = () => {
      const u = new URL(window.location.href);
      u.searchParams.delete("trial_card");
      window.history.replaceState({}, "", u.toString());
    };
    const refreshMe = async () => {
      const me = await fetch(`${API_BASE}/auth/me`, { credentials: "include" })
        .then((x) => x.json()).catch(() => null);
      if (me?.user) setUser(me.user);
      return me?.user?.pro_preview_days_left != null;
    };
    if (p === "ok") {
      try { sessionStorage.removeItem(TRIALCARD_PENDING_KEY); } catch { /* ignore */ }
      captureEvent("trial_card_registered", {});
      captureEvent("trial_started", { mode: "card" });
      setToast({ type: "success", message: t("trial.sheet.started") });
      void refreshMe().then((active) => {
        if (!active) setTimeout(() => void refreshMe(), 2500);
      });
      strip();
      return;
    }
    if (p === "cancel") {
      try { sessionStorage.removeItem(TRIALCARD_PENDING_KEY); } catch { /* ignore */ }
      setToast({ type: "error", message: t("pricing.trial_card.abandoned") });
      strip();
      return;
    }
    // Bare Back-button return: marker survives, no params.
    try {
      if (sessionStorage.getItem(TRIALCARD_PENDING_KEY) === "1") {
        sessionStorage.removeItem(TRIALCARD_PENDING_KEY);
        if (user?.pro_preview_days_left == null) {
          setToast({ type: "error", message: t("pricing.trial_card.abandoned") });
        }
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, t]);

  // Back-button return from the Epoint form: no ?payment/?sub params at
  // all, only the sessionStorage marker survives. Confirm against
  // /checkout/last-payment (an abandon stays "pending"; a real decline
  // would have come back with params) and show the no-charge toast.
  // pageshow(persisted) covers the bfcache restore, where effects
  // wouldn't re-run.
  useEffect(() => {
    const check = () => {
      try {
        if (sessionStorage.getItem(CHECKOUT_PENDING_KEY) !== "1") return;
        if (searchParams?.get("payment") || searchParams?.get("sub")) return; // param paths own it
        sessionStorage.removeItem(CHECKOUT_PENDING_KEY);
      } catch {
        return;
      }
      fetch(`${API_BASE}/checkout/last-payment`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((p: LastPayment | null) => {
          if (!p || p.status === "pending" || p.status === "none") {
            setToast({ type: "error", message: t("pricing.payment.abandoned") });
          }
        })
        .catch(() => { /* stay quiet - nothing to claim */ });
    };
    check();
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) check(); };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, t]);

  // Return from a subscription checkout. The Epoint card-callback flips
  // tier=paid server-side (may lag a beat). On success we fire the same
  // confetti + a Pro-specific success modal (mirroring the token-pack
  // flow), refresh tokens, and strip the marker. The Pro card shows the
  // current-plan strip once /subscription/status reflects the change.
  useEffect(() => {
    const sub = searchParams?.get("sub");
    if (sub !== "ok" && sub !== "cancel") return;
    try { sessionStorage.removeItem(CHECKOUT_PENDING_KEY); } catch { /* ignore */ }
    if (sub === "ok") {
      captureEvent("pro_subscribe_returned");
      refreshTokens?.();
      setShowConfetti(true);
      setTimeout(() => setShowProSuccess(true), 150);
    }
    const u = new URL(window.location.href);
    u.searchParams.delete("sub");
    window.history.replaceState({}, "", u.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, t]);

  // Pull subscription details (renewal date + masked card) for active
  // subscribers so the Pro card can show "Current plan · renews <date>
  // · •••• <card>". Re-fetched whenever the subscriber flag flips (e.g.
  // right after a successful checkout).
  useEffect(() => {
    if (!user || !(isSubscriber || onTrialNow)) {
      setSubInfo(null);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/subscription/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((info: SubscriptionInfo | null) => {
        if (!cancelled) setSubInfo(info);
      })
      .catch(() => {
        /* swallow - card just renders without the current-plan strip */
      });
    return () => {
      cancelled = true;
    };
  }, [user, isSubscriber]);

  // The subscriber's own numbers. Four small reads, only for people who
  // are actually subscribed, so the sales page pays nothing for them.
  const [proCounts, setProCounts] = useState<{
    analyses: number | null; alerts: number | null;
    watchlist: number | null; properties: number | null;
  }>({ analyses: null, alerts: null, watchlist: null, properties: null });

  useEffect(() => {
    if (!user || !isSubscriber) return;
    let cancelled = false;
    const get = (path: string) =>
      fetch(`${API_BASE}${path}`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    void Promise.all([
      get("/history?page_size=1"),
      get("/alerts"),
      get("/watchlist"),
      get("/properties"),
    ]).then(([h, a, w, pr]) => {
      if (cancelled) return;
      setProCounts({
        analyses: h?.total ?? null,
        alerts: a?.items?.length ?? null,
        watchlist: w?.items?.length ?? null,
        properties: pr?.items?.length ?? null,
      });
    });
    return () => { cancelled = true; };
  }, [user, isSubscriber]);

  const closeSuccess = () => {
    setShowProSuccess(false);
    setShowConfetti(false);
  };

  // Format an ISO date as "15 iyun 2026" via the hand-mapped month
  // table. Intl is NOT safe here: Azerbaijani month names collapse to
  // "M05"-style tokens on some ICU builds (the exact bug the user saw
  // as "2026 M05 26" on the renewal line).
  const formatRenewDate = (iso: string | null): string =>
    iso ? formatPublishedAt(iso, locale) : "";

  const subscribe = async () => {
    if (!user) {
      router.push(`/${locale}/auth/login?return_to=${encodeURIComponent("/pricing")}`);
      return;
    }
    const plan = isAnnual ? "pro_yearly" : "pro_monthly";
    setBusy(true);
    setToast(null);
    trackEvent("pricing.pro.subscribe", { plan });
    captureEvent("pro_subscribe_initiated", { plan, price_azn: proPrice });
    try {
      const origin = window.location.origin;
      const res = await fetch(`${API_BASE}/subscription/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Locale": locale },
        body: JSON.stringify({
          plan,
          return_url: `${origin}/${locale}/pricing?sub=ok`,
          cancel_url: `${origin}/${locale}/pricing?sub=cancel`,
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        const msg =
          (detail && typeof detail === "object" && "detail" in detail
            ? String((detail as { detail?: unknown }).detail)
            : null) || `HTTP ${res.status}`;
        throw new Error(msg);
      }
      const body = (await res.json()) as { url?: string };
      if (!body.url) throw new Error("Missing checkout url");
      // Hard navigation: the checkout URL is the bank's page, which fails
      // inside an iframe (blocked third-party cookie), so no in-app modal.
      try { sessionStorage.setItem(CHECKOUT_PENDING_KEY, "1"); } catch { /* ignore */ }
      window.location.href = body.url;
    } catch (e) {
      setBusy(false);
      setToast({
        type: "error",
        message: e instanceof Error ? e.message : t("pricing.payment.failed"),
      });
    }
  };

  // Start the 7-day trial straight from the Pro card (the video-style
  // paywall: the trial IS the primary flow for eligible accounts).
  // Anonymous visitors route to register - new accounts are eligible.
  // Card mode: register the card first (no charge today) - the trial
  // activates when Epoint's callback confirms the registration.
  const cardMode = (user?.trial_mode ?? trialMode) === "card";
  const startTrial = async () => {
    if (!user) {
      router.push(`/${locale}/auth/register?return_to=${encodeURIComponent("/pricing")}`);
      return;
    }
    setTrialBusy(true);
    try {
      if (cardMode) {
        captureEvent("trial_card_registration_started", {
          plan: isAnnual ? "pro_yearly" : "pro_monthly",
        });
        const r = await fetch(`${API_BASE}/auth/trial/start-with-card`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            plan: isAnnual ? "pro_yearly" : "pro_monthly",
            origin: window.location.origin,
            locale,
          }),
        });
        const d = await r.json().catch(() => null);
        if (r.ok && d?.ok && d.url) {
          try { sessionStorage.setItem(TRIALCARD_PENDING_KEY, "1"); } catch { /* ignore */ }
          window.location.href = d.url;
          return; // navigating away
        }
        setToast({ type: "error", message: t("pricing.trial_card.unavailable") });
        return;
      }
      const r = await fetch(`${API_BASE}/auth/trial/start`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json().catch(() => null);
      if (r.ok && d?.ok) {
        captureEvent("trial_started", { mode: "nocard", plan: null });
        const me = await fetch(`${API_BASE}/auth/me`, { credentials: "include" })
          .then((x) => x.json()).catch(() => null);
        if (me?.user) setUser(me.user);
        setToast({ type: "success", message: t("trial.sheet.started") });
      }
    } finally {
      setTrialBusy(false);
    }
  };

  // End the running Pro trial early. Honest and boring on purpose:
  // there is no card and no charge to stop - features just revert to
  // the free plan immediately.
  const cancelTrial = async () => {
    if (!(await confirmDlg({
      body: t("pricing.trial.end_confirm"),
      cancelLabel: t("confirm.trial.keep"),
      confirmLabel: t("confirm.trial.go"),
      danger: true,
    }))) return;
    setTrialBusy(true);
    try {
      const r = await fetch(`${API_BASE}/auth/trial/cancel`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json().catch(() => null);
      if (r.ok && d?.ok) {
        captureEvent("trial_canceled", { mode: cardMode ? "card" : "nocard" });
        const me = await fetch(`${API_BASE}/auth/me`, { credentials: "include" })
          .then((x) => x.json()).catch(() => null);
        if (me?.user) setUser(me.user);
        refreshTokens?.();
        setToast({ type: "success", message: t("pricing.trial.end_done") });
      }
    } finally {
      setTrialBusy(false);
    }
  };

  // The Pro card leads with the FREE TRIAL whenever this viewer can
  // still have one (video-reference paywall): anonymous visitors (a new
  // account is eligible) and signed-in eligible users. On-trial, paid
  // and trial-spent accounts see the subscribe framing instead.
  const trialPitch =
    !isSubscriber &&
    user?.pro_preview_days_left == null &&
    (!user || (user.pro_trial_eligible === true && user.email_verified !== false));

  // Card trial actually RUNNING (authoritative signal: the trialing
  // subscription shell the callback created). Drives the state card's
  // charge-facts copy instead of the nocard loss list.
  const isCardTrial = onTrialNow && subInfo?.status === "trialing";
  const cardTrialAmount =
    subInfo?.plan === "pro_yearly" ? PRO_PRICE_ANNUAL_TOTAL : PRO_PRICE_MONTHLY;
  // The price the CTA-adjacent copy quotes for the pitch (selected plan).
  const pitchAmount = isAnnual ? PRO_PRICE_ANNUAL_TOTAL : PRO_PRICE_MONTHLY;

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader />

      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-full px-4">
          <div
            className={`px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 ${
              toast.type === "success"
                ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : "bg-rose-50 text-rose-800 border border-rose-200"
            }`}
            role="status"
          >
            <span className="flex-1">{toast.message}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="shrink-0 p-1 rounded-lg text-current opacity-50 hover:opacity-100 hover:bg-black/5 transition"
              aria-label={t("common.dismiss")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Blinkist-typographic paywall: no imagery, no card chrome up
          top - an oversized benefit headline, the day-timeline blown up
          into THE page graphic, one giant CTA, price as a quiet line
          under it. Everything else (the features) lives below
          the fold in the app's standard idiom. */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-12">
        {(user?.pro_preview_days_left != null ||
          (user?.pro_preview_ended_days_ago != null && !isSubscriber) ||
          (subInfo?.status === "canceled" && subInfo.ends_at)) && (
          <div className="mb-8 space-y-3">
            {/* Active trial: a real state card, not a text strip - countdown
                chip, 7-day progress, keep-Pro CTA and an honest one-click
                way OUT (no card was ever taken, so ending it costs nothing
                and saying so builds trust). */}
            {user?.pro_preview_days_left != null && (() => {
              const daysLeft = user.pro_preview_days_left;
              // A granted month (feedback-for-Pro) runs against 30 days,
              // the week against 7, and ending the week would not end
              // the month: no end-trial control for a bonus.
              const isBonus = user.pro_preview_kind === "bonus";
              const totalDays = Math.max(user.pro_preview_total_days ?? 7, daysLeft, 1);
              // Final-2-days urgency: the card shifts from calm white to
              // amber, the chip flips to "last N days" copy, and the end
              // DATE gets named - a deadline you can point at beats an
              // abstract countdown.
              const urgent = daysLeft <= 2;
              const endsOn = formatMonthDay(Date.now() + daysLeft * 86_400_000, locale);
              return (
              <div className={`rounded-2xl border p-4 sm:p-5 ${
                urgent ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-white"
              }`}>
                {/* One clean header row (chip left, deadline right),
                    progress bar full-width BELOW - the old chip|bar|date
                    single row wrapped into three ragged lines on
                    phones and squeezed on desktop. */}
                {/* The person just said yes: tell them it was a good call
                    before the countdown (owner, 2026-10-10). */}
                {!urgent && (
                  <p className="mb-2.5 text-base font-bold leading-snug text-gray-900">
                    {t("pricing.trial.praise").replace("{n}", String(daysLeft))}
                  </p>
                )}
                <div className="flex items-center justify-between gap-3">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                    urgent ? "bg-[#d97706] text-white" : "bg-gray-900 text-gray-50"
                  }`}>
                    <Crown className="w-3.5 h-3.5" />
                    {t(urgent ? "trial.chip.last" : "trial.chip").replace("{n}", String(daysLeft))}
                  </span>
                  <span className={`text-[11px] font-semibold tabular-nums ${
                    urgent ? "text-amber-800" : "text-gray-500"
                  }`}>
                    {t("pricing.trial.ends_on").replace("{date}", endsOn)}
                  </span>
                </div>
                <div className={`mt-2.5 h-1.5 w-full rounded-full bg-white border overflow-hidden ${
                  urgent ? "border-amber-200" : "border-gray-200"
                }`}>
                  <div
                    className={`h-full rounded-full transition-all ${urgent ? "bg-[#d97706]" : "bg-gray-900"}`}
                    style={{ width: `${Math.round((daysLeft / totalDays) * 100)}%` }}
                  />
                </div>
                {/* Personal stakes FIRST - what ends for THIS account, by
                    name and count (the /trial/losses source also feeds the
                    day-5 email, so nothing here is theater). The counted,
                    it's-yours rows lead in bold; the feature gates every
                    account shares follow quieter. */}
                {isCardTrial ? (
                  <div className="mt-3 rounded-xl bg-gray-50 border border-gray-200 p-3 text-xs text-gray-800">
                    {t("pricing.trial.card_facts")
                      .replace("{price}", String(cardTrialAmount))
                      .replace(
                        "{date}",
                        subInfo?.ends_at
                          ? formatMonthDay(new Date(subInfo.ends_at).getTime(), locale)
                          : "",
                      )}
                    {subInfo?.card ? ` · ${subInfo.card}` : ""}
                  </div>
                ) : (() => {
                  const personal: string[] = [];
                  if ((losses?.alerts_over_cap ?? 0) > 0) {
                    personal.push(
                      t("pricing.loss.alerts")
                        .replace("{total}", String(losses?.alerts_active ?? 0))
                        .replace("{over}", String(losses?.alerts_over_cap ?? 0)),
                    );
                  }
                  if ((losses?.alerts_instant ?? 0) > 0) {
                    personal.push(t("pricing.loss.instant"));
                  }
                  if ((losses?.pins_over_cap ?? 0) > 0 || (losses?.properties_over_cap ?? 0) > 0) {
                    personal.push(t("pricing.loss.limits"));
                  }
                  if ((losses?.history_over_cap ?? 0) > 0) {
                    personal.push(
                      t("pricing.loss.history_rows")
                        .replace("{n}", String(losses?.history_over_cap ?? 0)),
                    );
                  }
                  // Losses every trial account shares: these feature
                  // gates really do close at expiry for everyone.
                  const generic = [
                    t("pricing.loss.history"),
                    t("pricing.loss.photos"),
                    t("pricing.loss.mymap"),
                  ];
                  const tone = urgent ? "text-amber-900" : "text-gray-800";
                  return (
                    <div className="mt-3">
                      <p className={`text-[11px] font-bold mb-1.5 ${tone}`}>
                        {t("pricing.loss.title")}
                      </p>
                      <ul className="space-y-1">
                        {personal.map((r, i) => (
                          <li key={`p${i}`} className={`flex items-start gap-2 text-xs font-semibold leading-relaxed ${tone}`}>
                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#d97706] shrink-0" aria-hidden />
                            {r}
                          </li>
                        ))}
                        {generic.map((r, i) => (
                          <li key={`g${i}`} className={`flex items-start gap-2 text-xs leading-relaxed opacity-80 ${tone}`}>
                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-400 shrink-0" aria-hidden />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
                {/* Reassurance AFTER the stakes, as quiet small print: no
                    card on file, ending costs nothing - the trust line
                    stays, it just no longer leads the story. */}
                <p className={`mt-2.5 text-[11px] leading-relaxed ${urgent ? "text-amber-800/80" : "text-gray-600"}`}>
                  {isCardTrial
                    ? t("pricing.trial.body.card")
                        .replace("{price}", String(cardTrialAmount))
                        .replace(
                          "{date}",
                          subInfo?.ends_at
                            ? formatMonthDay(new Date(subInfo.ends_at).getTime(), locale)
                            : "",
                        )
                    : t("pricing.trial.body")}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  {/* "Keep Pro" appears only when the trial is actually
                      ending (<=2 days left). On day one it read as
                      "you must pay now" - users clicked, hit the
                      payment screen and bounced. Early trial is for
                      USING Pro, not for being sold it. */}
                  {!inApp && !isCardTrial &&
                    (user?.pro_preview_days_left ?? 99) <= 2 && (
                    <button
                      type="button"
                      onClick={subscribe}
                      disabled={busy}
                      className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {t("pricing.trial.keep_cta")}
                    </button>
                  )}
                  {!isBonus && (
                    <button
                      type="button"
                      onClick={cancelTrial}
                      disabled={trialBusy}
                      className="text-xs font-medium text-gray-500 hover:text-gray-700 underline underline-offset-2 disabled:opacity-50"
                    >
                      {trialBusy ? "..." : t("pricing.trial.end_link")}
                    </button>
                  )}
                </div>
              </div>
              );
            })()}

            {user?.pro_preview_ended_days_ago != null && !isSubscriber && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5">
                {/* The lights-out vignette: Pro chips dim one by one,
                    the crown pulses - the loss story, drawn. */}
                <div className="h-14 w-24 shrink-0 self-center">
                  <TrialDimAnim />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-amber-900">{t("pricing.trial_ended.title")}</p>
                  <p className="mt-0.5 text-xs text-amber-800 leading-relaxed">{t("pricing.trial_ended.body")}</p>
                </div>
                {!inApp && (
                  <button
                    type="button"
                    onClick={subscribe}
                    disabled={busy}
                    className="shrink-0 rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
                  >
                    {t("pricing.trial_ended.cta")}
                  </button>
                )}
              </div>
            )}

            {/* Loss-framed renewal banner for CANCELED-but-still-active Pro:
                what closes, by name, with the date - all true (features
                gate on tier, which flips at period end). */}
            {subInfo?.status === "canceled" && subInfo.ends_at && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-amber-900">
                    {t("pricing.expiry.title").replace(
                      "{date}",
                      formatMonthDay(new Date(subInfo.ends_at).getTime(), locale),
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-amber-800 leading-relaxed">
                    {t("pricing.expiry.body")}
                  </p>
                </div>
                {!inApp && (
                  <button
                    type="button"
                    onClick={subscribe}
                    disabled={busy}
                    className="shrink-0 rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
                  >
                    {t("pricing.expiry.cta")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Hero ── */}
        <div className="relative text-center">
          {trialPitch && !inApp ? (
            <>
              {/* Soft brand glow behind the pitch - gives the paywall a
                  stage without imagery. Pure decoration: pointer-inert,
                  behind everything, fades to nothing at the edges. */}
              <div
                aria-hidden
                className="pointer-events-none absolute -top-16 left-1/2 -z-10 h-72 w-[36rem] max-w-[100vw] -translate-x-1/2 rounded-full opacity-60"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(99,102,241,0.22), rgba(139,92,246,0.10) 55%, transparent 75%)",
                }}
              />
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-[1.1] text-balance">
                {t("pricing.hero.trial")}
              </h1>
              {/* The no-card promise is the objection-killer - a badge,
                  not a gray whisper. */}
              <p className="mt-4 flex justify-center">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-sm font-bold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                  <ShieldCheck className="h-4 w-4" />
                  {cardMode ? t("pricing.hero.trial_sub_card") : t("pricing.hero.trial_sub")}
                </span>
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                {t("pricing.title")}
              </h1>
              <p className="mt-3 text-base text-gray-600 leading-relaxed">
                {isSubscriber
                  ? t("pricing.subtitle.subscriber")
                  : user ? t("pricing.subtitle.signedin") : t("pricing.subtitle")}
              </p>
            </>
          )}
        </div>

        {/* ── CTA zone ── */}
        <div className="mx-auto mt-8 max-w-md">
          {planUnknown ? (
            <div className="h-44 animate-pulse rounded-2xl border border-gray-200 bg-white" />
          ) : inApp && !isSubscriber ? (
            <p className="w-full py-3 text-center text-sm text-gray-500">
              {t("pricing.app_notice")}
            </p>
          ) : isSubscriber ? (
            /* The subscriber's card. Only facts about THIS subscription
               (the page's H1 already names the product), but the one
               they came for - the next payment date - carries the
               weight, so the card reads as a statement rather than a
               settings row (owner, 2026-09-22: "you made it too
               minimalized, make it a bit more outstanding"). */
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
                  <Crown className="h-3.5 w-3.5 text-gray-500" />
                  {t("pricing.pro.sub_label")}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                    subInfo?.status === "canceled"
                      ? "bg-amber-50 text-amber-700"
                      : "bg-emerald-50 text-emerald-700"
                  }`}
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {subInfo?.status === "canceled"
                    ? t("pricing.pro.status.canceled")
                    : t("pricing.pro.status.active")}
                </span>
              </div>

              <div className="px-5 py-5">
                {subInfo?.ends_at && (
                  <>
                    <p className="text-[11px] font-semibold text-gray-500">
                      {subInfo.status === "canceled"
                        ? t("pricing.pro.row.until")
                        : t("pricing.pro.row.next")}
                    </p>
                    <p className="mt-0.5 text-3xl font-extrabold tracking-tight tabular-nums text-gray-900">
                      {formatRenewDate(subInfo.ends_at)}
                    </p>
                  </>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px]">
                  {subInfo?.plan && (
                    <span className="inline-flex items-baseline gap-1.5">
                      <span className="text-gray-400">{t("pricing.pro.row.plan")}</span>
                      <span className="font-bold tabular-nums text-gray-900">
                        {subInfo.plan === "pro_yearly"
                          ? t("pricing.pro.plan.yearly")
                          : t("pricing.pro.plan.monthly")}
                        <span className="font-medium text-gray-400">
                          {" · "}
                          {subInfo.plan === "pro_yearly" ? PRO_PRICE_ANNUAL_TOTAL : PRO_PRICE_MONTHLY} AZN
                        </span>
                      </span>
                    </span>
                  )}
                  {subInfo?.card && (
                    <span className="inline-flex items-center gap-1.5">
                      <CreditCard className="h-3.5 w-3.5 text-gray-400" />
                      <span className="font-semibold tabular-nums text-gray-700">{subInfo.card}</span>
                    </span>
                  )}
                </div>
              </div>

              {subInfo && subInfo.status !== "canceled" && (
                <div className="border-t border-gray-100 px-5 py-2.5">
                  <button
                    type="button"
                    onClick={cancelSubscription}
                    disabled={canceling}
                    className="text-xs font-medium text-gray-500 underline-offset-2 hover:text-rose-600 hover:underline disabled:opacity-50"
                  >
                    {canceling ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : t("profile.cancel")}
                  </button>
                </div>
              )}
            </div>
          ) : trialPitch ? (
            <>
              <button
                type="button"
                onClick={startTrial}
                disabled={trialBusy}
                className="w-full rounded-2xl bg-indigo-600 px-6 py-4 text-base font-bold text-white shadow-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {trialBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    {t("trial.offer.cta")}
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
              <p className="mt-3.5 text-center text-sm text-gray-500">
                {cardMode
                  ? t("pricing.hero.after.card").replace("{price}", String(pitchAmount))
                  : t("pricing.hero.after").replace("{price}", String(PRO_PRICE_ANNUAL_PER_MONTH))}
              </p>
            </>
          ) : (
            // Premium plan card: a thin dark ring around the white body
            // (the flat gray border read as a generic form card; dark, not
            // brand, since the quiet UI), the product name up top so the
            // card is ABOUT something, and the price as the typographic
            // hero.
            <div className="rounded-3xl bg-gray-900 p-[1.5px] shadow-xl">
            <div className="rounded-[calc(1.5rem-1.5px)] bg-white p-5 sm:p-6">
              <div className="mb-4 flex items-center justify-center gap-1.5">
                <Crown className="h-4 w-4 text-gray-700" />
                <span className="text-sm font-extrabold tracking-tight text-gray-900">
                  Mənzil.ai Pro
                </span>
              </div>
              <div className="mb-4 flex justify-center">
                <div
                  ref={segMountRef}
                  role="radiogroup"
                  aria-label={t("pricing.pro.monthly") + " / " + t("pricing.pro.annually")}
                  className="relative inline-flex items-center gap-1 p-1 rounded-full border border-gray-200 bg-gray-50"
                >
                  {/* Sliding thumb - the only element that moves. */}
                  {thumb && (
                    <span
                      aria-hidden
                      className={`absolute top-1 bottom-1 left-0 rounded-full ${SEGMENT_ON} transition-[transform,width] duration-200 ease-out motion-reduce:transition-none`}
                      style={{ transform: `translateX(${thumb.x}px)`, width: thumb.w }}
                    />
                  )}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!isAnnual}
                    data-billing="monthly"
                    onClick={() => {
                      trackEvent("pricing.billing.toggle", { to: "monthly" });
                      setBilling("monthly");
                    }}
                    className={`relative px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors duration-200 ${
                      !isAnnual ? "text-indigo-700" : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {t("pricing.pro.monthly")}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isAnnual}
                    data-billing="annual"
                    onClick={() => {
                      trackEvent("pricing.billing.toggle", { to: "annual" });
                      setBilling("annual");
                    }}
                    className={`relative px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors duration-200 inline-flex items-center gap-1.5 ${
                      isAnnual ? "text-indigo-700" : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {t("pricing.pro.annually")}
                    {/* border in BOTH states so the chip's box never
                        changes size (a 1px border used to appear and
                        disappear, nudging the whole control). */}
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                      {t("pricing.billing.save")}
                    </span>
                  </button>
                </div>
              </div>
              <div className="text-center">
                {/* min-h pins the row's height; the struck anchor stays
                    mounted and collapses via max-width so the price
                    slides instead of jumping when billing flips. */}
                <div className="flex min-h-12 items-baseline justify-center gap-2">
                  <span
                    aria-hidden={!isAnnual}
                    className={`inline-flex overflow-hidden whitespace-nowrap transition-all duration-200 ease-out motion-reduce:transition-none ${
                      isAnnual ? "max-w-24 opacity-100" : "max-w-0 opacity-0"
                    }`}
                  >
                    <span className="text-base font-semibold text-gray-400 line-through">
                      {PRO_PRICE_MONTHLY} AZN
                    </span>
                  </span>
                  <span className="text-5xl font-extrabold tracking-tight text-gray-900 tabular-nums">{proPrice}</span>
                  <span className="text-lg font-bold text-gray-900">AZN</span>
                  <span className="text-sm text-gray-500">/{t("pricing.pro.permonth")}</span>
                </div>
                {/* Sub-line as two quiet chips, not a run-on sentence -
                    the per-day reframe ("less than 1 AZN a day") is the
                    strongest line here, give it its own capsule. */}
                {/* min-h so the card below never moves when the second
                    chip's text (and width) changes with the billing. */}
                <div className="mt-3 flex min-h-[26px] flex-wrap items-center justify-center gap-1.5">
                  <span className="whitespace-nowrap rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    {t("pricing.pro.perday")}
                  </span>
                  <span className="whitespace-nowrap rounded-full bg-gray-50 border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-600">
                    {isAnnual
                      ? t("pricing.pro.annual.billed").replace("{price}", String(PRO_PRICE_ANNUAL_TOTAL))
                      : t("pricing.pro.monthly_hint").replace("{price}", String(PRO_PRICE_ANNUAL_PER_MONTH))}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={subscribe}
                disabled={busy}
                className="mt-5 w-full rounded-2xl bg-indigo-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    {/* On a trial the button keeps what is already on, it
                        never asks to start again (owner, 2026-10-10). */}
                    {user?.pro_preview_days_left != null
                      ? t("pricing.trial.keep_cta")
                      : t("pricing.pro.cta")}
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
            </div>
          )}
        </div>

        {/* ── The timeline IS the graphic (Blinkist: transparency over
              sell). It renders for EVERY non-subscriber state:
              - can-start-trial: the 3-day story, day 0 pre-filled
                (goal-gradient);
              - on trial: the same story with the steps they have
                already passed filled in - live progress, not a pitch;
              - trial spent: the 2-step subscribe story (instant
                unlock, cancel anytime) in the same visual language. ── */}
        {!isSubscriber && !planUnknown && !inApp && (() => {
          // The 7-day story is the opt-in week's; a granted month shows
          // the subscribe story like any account whose week is spent.
          const onTrial = user?.pro_preview_days_left != null && user?.pro_preview_kind !== "bonus";
          const elapsed = onTrial ? 7 - (user?.pro_preview_days_left ?? 7) : 0;
          const steps: {
            key: string;
            labelKey: TranslationKey;
            textKey: TranslationKey;
            circle: string;
            labelCls: string;
            Icon: typeof Check | null;
          }[] = (trialPitch || onTrial)
            ? [
                {
                  key: "d0",
                  labelKey: "trial.offer.day0.label",
                  textKey: "trial.offer.day0",
                  circle: "bg-emerald-500 text-white",
                  labelCls: "text-emerald-600",
                  Icon: Check,
                },
                {
                  key: "d5",
                  labelKey: "trial.offer.day5.label",
                  textKey: (cardMode && trialPitch ? "trial.offer.day5.card" : "trial.offer.day5") as TranslationKey,
                  circle: onTrial && elapsed < 5
                    ? "border-2 border-amber-300 bg-white text-amber-500"
                    : "bg-amber-400 text-white",
                  labelCls: "text-amber-600",
                  Icon: Bell,
                },
                {
                  key: "d7",
                  labelKey: "trial.offer.day7.label",
                  textKey: (cardMode && trialPitch ? "trial.offer.day7.card" : "trial.offer.day7") as TranslationKey,
                  circle: "border-2 border-gray-300 bg-white",
                  labelCls: "text-gray-500",
                  Icon: null,
                },
              ]
            : [
                {
                  key: "s0",
                  labelKey: "trial.offer.day0.label",
                  textKey: "pricing.step.today",
                  circle: "bg-emerald-500 text-white",
                  labelCls: "text-emerald-600",
                  Icon: Check,
                },
                {
                  key: "s1",
                  labelKey: "pricing.step.anytime.label",
                  textKey: "pricing.pro.cancel_note",
                  circle: "border-2 border-gray-200 bg-white text-gray-500",
                  labelCls: "text-gray-600",
                  Icon: ShieldCheck,
                },
              ];
          return (
            <ol className="mx-auto mt-8 sm:mt-10 max-w-md">
              {steps.map(({ key, labelKey, textKey, circle, labelCls, Icon }, i) => {
                // Day steps carry their NUMBER tall in the circle
                // (owner call: the icon-in-circle read as decoration;
                // "5" and "7" are the actual information). The day-0
                // step keeps the check - it's a state, not a date.
                const dayNum = /^d[1-9]/.test(key) ? key.slice(1) : null;
                return (
                <li key={key} className="flex gap-3.5 sm:gap-4">
                  <div className="flex flex-col items-center">
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${circle}`}
                      aria-hidden
                    >
                      {dayNum ? (
                        <span className="text-lg font-extrabold leading-none tabular-nums">{dayNum}</span>
                      ) : (
                        Icon && <Icon className="w-[18px] h-[18px]" strokeWidth={2.5} />
                      )}
                    </span>
                    {i < steps.length - 1 && <span className="w-0.5 flex-1 bg-gray-200 my-1" aria-hidden />}
                  </div>
                  {/* Uniform rhythm: same bottom padding per step at every
                      breakpoint, label optically centred on the circle. */}
                  <div className={i < steps.length - 1 ? "pb-6" : ""}>
                    <p className={`pt-2 text-xs font-bold ${labelCls}`}>
                      {t(labelKey)}
                    </p>
                    <p className="mt-1 text-sm sm:text-[15px] text-gray-700 leading-relaxed">
                      {t(textKey).replace("{price}", String(pitchAmount))}
                    </p>
                  </div>
                </li>
                );
              })}
            </ol>
          );
        })()}

        {/* ── What Pro holds. No boxed icon rows (the standard SaaS
            tell) - a plain staggered checklist carried by whitespace,
            revealed as it scrolls into view. ── */}
        {/* The "what Pro holds" checklist is a sales argument, so it
            stops at the sale: a subscriber gets their own numbers
            instead (owner, 2026-09-22: "we are repeating ... the things
            that are in the pro as well", then "it's very empty"). */}
        {!isSubscriber && !planUnknown && (
        <section className="mt-14 sm:mt-16 max-w-2xl mx-auto">
          {/* What everyone has, said once above the Pro list so the page
              never reads as if analyses were sold (owner, 2026-10-18:
              analyses are unlimited for every account; Pro sells
              features, not volume). */}
          <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-5">
            <h2 className="text-sm font-semibold text-gray-900">{t("pricing.free.everyone")}</h2>
            <ul className="mt-3 grid gap-x-10 gap-y-2 sm:grid-cols-2">
              {(["pricing.free.f1", "pricing.free.f2", "pricing.free.f3", "pricing.free.f4"] as const).map((key) => (
                <li key={key} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" strokeWidth={3} />
                  <p className="text-sm text-gray-700 leading-snug">{t(key)}</p>
                </li>
              ))}
            </ul>
          </div>
          <h2 className="text-sm text-gray-500 font-semibold mb-5 text-center">
            {t("pricing.pro.everything")}
          </h2>
          <ul className="grid gap-x-10 gap-y-3.5 sm:grid-cols-2">
            {/* Every Pro gate that ships, checked against the product
                facts in chat_tools._PRODUCT_FACTS (owner, 2026-10-10). */}
            {([
              "pricing.pro.f1",
              "pricing.pro.f5",
              "pricing.pro.f2",
              "pricing.pro.f3",
              "pricing.pro.f4",
              "pricing.pro.f6",
              "pricing.pro.f7",
              "pricing.pro.f9",
              "pricing.pro.f10",
              "pricing.pro.f11",
              "pricing.pro.f12",
              "pricing.pro.f13",
              "pricing.pro.f14",
              "pricing.pro.f15",
              "pricing.pro.f8",
            ] as const).map((key, i) => (
              <Reveal key={key} delay={i * 60}>
                <li className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-gray-700 mt-0.5 shrink-0" strokeWidth={3} />
                  <p className="text-sm text-gray-800 leading-snug">{t(key)}</p>
                </li>
              </Reveal>
            ))}
          </ul>
        </section>
        )}

        {/* What the subscriber actually has: their own counts, each a
            door into the surface that holds them. */}
        {isSubscriber && (
          <section className="mx-auto mt-12 max-w-2xl">
            <h2 className="mb-4 text-center text-sm font-semibold text-gray-500">
              {t("pricing.pro.yours")}
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {([
                { key: "profile.stats.analyses", n: proCounts.analyses, href: "/history", Icon: ScanEye },
                { key: "profile.stats.alerts", n: proCounts.alerts, href: "/alerts", Icon: Bell },
                { key: "profile.stats.watchlist", n: proCounts.watchlist, href: "/watchlist", Icon: Heart },
                { key: "profile.stats.properties", n: proCounts.properties, href: "/profile/properties", Icon: Building2 },
              ] as const).map(({ key, n, href, Icon }) => (
                <Link
                  key={key}
                  href={href}
                  className="group rounded-2xl border border-gray-200 bg-white p-4 transition-all hover:border-gray-300"
                >
                  <Icon className="h-4 w-4 text-gray-300 transition-colors group-hover:text-gray-500" />
                  <div className="mt-2 text-3xl font-extrabold tracking-tight tabular-nums text-gray-900">
                    {n == null ? "-" : n}
                  </div>
                  <div className="mt-0.5 text-[11px] font-medium text-gray-500">{t(key)}</div>
                </Link>
              ))}
            </div>

            <h2 className="mb-4 mt-10 text-center text-sm font-semibold text-gray-500">
              {t("pricing.pro.tools")}
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {([
                { key: "nav.analyze", href: "/analyze", Icon: ScanEye },
                { key: "nav.compare", href: "/compare", Icon: CopyIcon },
                { key: "nav.map", href: "/map", Icon: MapIcon },
                { key: "nav.chat", href: "/chat", Icon: MessageSquare },
              ] as const).map(({ key, href, Icon }) => (
                <Link
                  key={key}
                  href={href}
                  className="group flex items-center gap-2.5 rounded-2xl border border-gray-200 bg-white px-3.5 py-3 text-[13px] font-semibold text-gray-700 transition-all hover:border-gray-300 hover:text-gray-900"
                >
                  <Icon className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-700" />
                  <span className="min-w-0 truncate">{t(key)}</span>
                  <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-gray-300" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Price transparency for the trial pitch: the plan they'd keep
            afterwards, stated plainly (no toggle games pre-trial). */}
        {trialPitch && !inApp && (
          <p className="mt-6 text-center text-xs text-gray-400">
            {t("pricing.pro.annual.billed").replace("{price}", String(PRO_PRICE_ANNUAL_TOTAL))} · {t("pricing.pro.perday")}
          </p>
        )}

      </main>

      {/* Success path: confetti burst (one-shot, gravity tuned for a
          quick celebratory feel) and the Pro modal below. The modal
          closes on backdrop click or the OK button; closing also stops
          the confetti. */}
      {showConfetti && winSize.w > 0 && (
        <Confetti
          width={winSize.w}
          height={winSize.h}
          recycle={false}
          numberOfPieces={350}
          // Snappier burst per user feedback - higher initial
          // velocity gets the pieces visible immediately rather
          // than crawling out from the top edge, and a heavier
          // gravity makes the whole effect resolve in ~2 s instead
          // of dragging on for ~5 s.
          gravity={0.55}
          initialVelocityY={45}
          colors={["#4f46e5", "#7c3aed", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#06B6D4"]}
          style={{ position: "fixed", top: 0, left: 0, zIndex: 70, pointerEvents: "none" }}
        />
      )}

      {/* Pro-subscription success modal: Pro-specific copy and a Crown
          badge. (The token-pack twin that stood above it went with the
          token system, owner 2026-10-18.) */}
      {showProSuccess && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
          onClick={closeSuccess}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative z-[62] w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
              <Crown className="w-8 h-8 text-gray-900" />
            </div>

            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
              {t("pricing.pro.success.title")}
            </h2>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              {t("pricing.pro.success.body")}
            </p>

            <button
              type="button"
              onClick={closeSuccess}
              className="w-full py-3 bg-indigo-600 text-white rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-colors"
            >
              {t("welcome.cta")}
            </button>
          </div>
        </div>
      )}
      {confirmEl}
    </div>
  );
}
